import { useTranslation } from 'react-i18next';
import ProductCard from './ProductCard';
import type { Product } from './ProductCard';

interface FeaturedProductsSectionProps {
  products: Product[];
  onRequestQuote: (name: string) => void;
}

export default function FeaturedProductsSection({ products, onRequestQuote }: FeaturedProductsSectionProps) {
  const { t } = useTranslation('common');

  if (products.length === 0) return null;

  const [mainProduct, ...rest] = products;

  return (
    <section className="py-8 md:py-16 bg-gray-50/60">
      <div className="container mx-auto px-4 md:px-6">
        {/* Section Header */}
        <div className="flex items-center gap-3 mb-6 md:mb-10">
          <div className="w-1 h-8 bg-teal-600 rounded-full flex-shrink-0"></div>
          <div>
            <p className="text-xs font-semibold text-teal-600 uppercase tracking-widest mb-0.5">
              {t('products.handpickedSelection')}
            </p>
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900">{t('products.featuredProducts')}</h2>
          </div>
        </div>

        {/* Featured Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 lg:gap-8">
          {/* Main Featured Product */}
          <div className="relative group cursor-pointer rounded-2xl overflow-hidden bg-white border border-gray-100 flex flex-col min-h-[300px] md:min-h-[420px]">
            <div className="relative overflow-hidden flex-1 min-h-[180px] md:min-h-[260px]">
              <img
                src={mainProduct.image}
                alt={mainProduct.name}
                className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-700"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent"></div>
              {/* Category Badge */}
              <span className="absolute top-4 left-4 text-xs font-semibold px-3 py-1.5 rounded-full bg-white/90 text-gray-800">
                {mainProduct.category.charAt(0).toUpperCase() + mainProduct.category.slice(1)}
              </span>
              <span className="absolute top-4 right-4 bg-teal-600 text-white text-xs font-bold px-3 py-1.5 rounded-full">
                {t('products.featuredBadge')}
              </span>
            </div>
            <div className="p-6">
              <div className="flex items-center gap-1.5 mb-2">
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-medal-line text-teal-600 text-sm"></i>
                </div>
                <span className="text-xs font-semibold text-teal-700 uppercase tracking-wide">
                  {mainProduct.brand}
                </span>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">{mainProduct.name}</h3>
              <button
                onClick={() => onRequestQuote(mainProduct.name)}
                className="w-full bg-teal-600 text-white py-3 rounded-xl font-semibold hover:bg-teal-700 active:scale-95 transition-all duration-200 whitespace-nowrap cursor-pointer"
              >
                {t('products.requestQuote')}
              </button>
            </div>
          </div>

          {/* Side Featured Products */}
          <div className="grid grid-cols-2 gap-3 md:gap-5 content-start">
            {rest.slice(0, 3).map((product) => (
              <ProductCard
                key={product.id}
                product={product}
                onRequestQuote={onRequestQuote}
              />
            ))}
            {/* Banner card */}
            <div
              className="relative rounded-xl overflow-hidden flex items-end p-5 min-h-[160px]"
              style={{
                backgroundImage:
                  'url(https://public.readdy.ai/ai/img_res/4cfdd315f5272c789ff2578a0960e9bf.jpg)',
                backgroundSize: 'cover',
                backgroundPosition: 'center',
              }}
            >
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-black/20"></div>
              <div className="relative z-10">
                <p className="text-white/70 text-xs mb-1">GB Group</p>
                <p className="text-white font-bold text-sm leading-tight">
                  {t('products.genuineTagline')}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
